import React from 'react';
import { Box, Typography } from '@mui/material';
import { toLatin } from '../russianLetters';

// Минимальный рендер для грамматических заметок: абзацы, списки, **жирный**, *курсив*, ~~зачёркнутый~~.
// translit — после каждой русской фразы мелко показывается латиница (для итальянцев, изучающих русский).

const CYR = /[А-Яа-яЁё]/;
const WORDCHAR = /[А-Яа-яЁё́-]/;

// '**жи**ву' → [{t:'жи', b:true}, {t:'ву'}]
function parseInline(text) {
  const parts = text.split(/(\*\*[^*]+\*\*|~~[^~]+~~|\*[^*]+\*)/g).filter(Boolean);
  return parts.map((part) => {
    if (part.startsWith('**')) return { t: part.slice(2, -2), b: true };
    if (part.startsWith('~~')) return { t: part.slice(2, -2), s: true };
    if (part.startsWith('*') && part.length > 1) return { t: part.slice(1, -1), i: true };
    return { t: part };
  });
}

function styled(seg, key) {
  if (seg.b) return <strong key={key}>{seg.t}</strong>;
  if (seg.s) return <s key={key}>{seg.t}</s>;
  if (seg.i) return <em key={key}>{seg.t}</em>;
  return <React.Fragment key={key}>{seg.t}</React.Fragment>;
}

function renderInline(text, translit) {
  const segs = parseInline(text);
  if (!translit || !CYR.test(text)) return segs.map((s, i) => styled(s, i));

  // посимвольно: находим русские фразы поверх разметки и после каждой вставляем латиницу
  const chars = [];
  segs.forEach((s) => { for (const ch of s.t) chars.push({ ch, s }); });
  const out = [];
  let buf = null;
  let run = '';
  const flushBuf = () => { if (buf) { out.push(styled(buf, out.length)); buf = null; } };
  const push = (c) => {
    if (buf && buf.b === c.s.b && buf.i === c.s.i && buf.s === c.s.s) buf.t += c.ch;
    else { flushBuf(); buf = { ...c.s, t: c.ch }; }
  };
  const endRun = () => {
    if (run && CYR.test(run)) {
      flushBuf();
      out.push(
        <Typography key={out.length} component="span" variant="caption" color="text.secondary"
          sx={{ fontStyle: 'italic', ml: 0.5 }} lang="it">
          ({toLatin(run.trim())})
        </Typography>,
      );
    }
    run = '';
  };
  for (let k = 0; k < chars.length; k += 1) {
    const { ch } = chars[k];
    const isWord = WORDCHAR.test(ch);
    // пробел внутри фразы: дальше снова русское слово
    const nextIsCyr = chars[k + 1] && CYR.test(chars[k + 1].ch);
    if (isWord || (run && ch === ' ' && nextIsCyr)) {
      run += ch;
      push(chars[k]);
      continue;
    }
    endRun();
    push(chars[k]);
  }
  endRun();
  flushBuf();
  return out;
}

export default function MiniMarkdown({ text, translit = false }) {
  if (!text) return null;
  const blocks = [];
  let list = null;
  text.split('\n').forEach((raw) => {
    const line = raw.trimEnd();
    const bullet = line.match(/^(?:-|\d+\.)\s+(.*)$/);
    if (bullet) {
      if (!list) {
        list = { ordered: /^\d+\./.test(line), items: [] };
        blocks.push(list);
      }
      list.items.push(bullet[1]);
      return;
    }
    if (/^\s{2,}\S/.test(raw) && list) {
      // продолжение пункта списка (строка с отступом)
      list.items[list.items.length - 1] += `\n${line.trim()}`;
      return;
    }
    list = null;
    if (line.trim()) blocks.push({ p: line });
  });

  return (
    <Box>
      {blocks.map((b, i) => {
        if (b.p) {
          return (
            <Typography key={i} component="p" variant="body2" sx={{ my: 1 }}>
              {renderInline(b.p, translit)}
            </Typography>
          );
        }
        const Tag = b.ordered ? 'ol' : 'ul';
        return (
          <Box key={i} component={Tag} sx={{ pl: 3, my: 1 }}>
            {b.items.map((item, j) => (
              <Typography key={j} component="li" variant="body2" sx={{ whiteSpace: 'pre-line', mb: 0.5 }}>
                {renderInline(item, translit)}
              </Typography>
            ))}
          </Box>
        );
      })}
    </Box>
  );
}
